
import { Component, Prop, Vue } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import { UserRepoInterface } from '@/services/Users/Interfaces';
import DepartmentRepositoryInterface from '@/services/Departments/Interfaces';
import { FilterRequestData } from '@/services/QC-V2/chat/interfaces';
import { arch } from 'os';

interface FilterItemInterface {
  text: string,
  value: string | boolean | undefined,
}

enum FilterType {
  MENU,
  AUTOCOMPLETE,
  CHECKBOX,
  INPUT,
}

export interface FilterInterface {
  text: string,
  value: string,
  icon: string,
  type: FilterType,
  items: FilterItemInterface[],
  selected: FilterItemInterface,
}


@Component({
  mixins: [
    Themeable,
  ],
  inject: [
    'userRepository',
    'departmentRepository',
  ],
})
export default class TicketsFilters extends Vue {
  @Prop({ default: [] }) private filtersData: FilterInterface[];
  private userRepository!: UserRepoInterface;
  private departmentRepository!: DepartmentRepositoryInterface;

  private selectedFilters: FilterInterface[] = [];

  private filters: FilterInterface[] = [];

  private customDates: string[] = [];

  private departments: string[] = [
    'Support',
    'Inquiry',
    'Systems Admin',
    'Security & Optimization',
    'Billing',
    'Abuse',
    'WPX Domains - Failure',
  ];

  private async created(): Promise<void> {
    const filtersDataEmpty = this.filtersData.every((filter) => !filter.value);

    if (filtersDataEmpty) {
      await this.initialize();
    } else {
      this.filters = this.filtersData;
      this.selectedFilters = this.filters;
    }
  }

  private async initialize(): Promise<void> {
    try {
      this.addNumberFilter();
      this.addDatesFilter();
      await this.getSupportAgents();
      this.addDepartmentsFilter();
      this.addDraftsFilter();
      this.selectedFilters = this.filters;
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private keepCustomPeriodActive(filter: FilterInterface, index: number): void {
    setTimeout(() => {
      if (filter.value === 'ticket.closedDate' && filter.selected && filter.selected.value === 'custom period') {
        filter.selected = { text: 'CUSTOM PERIOD', value: 'custom period' };
        this.selectedFilters[index] = filter;
      }
    }, 10);
  }

  private get datesRangeText(): string {
    return this.customDates.join(' ~ ');
  }

  private async getSupportAgents(): Promise<void> {
    const departments = await this.departmentRepository.getAllDepartments();
    const supportDepartment = departments.find((department) => department.name.toLowerCase() === 'support');
    if (!supportDepartment) {
      this.$errorDialog.show('\'Support\' Department not found...');
      return;
    }

    const supportAgents = await this.userRepository.getUsersByDepartment(supportDepartment._id);
    this.filters.push(
      {
        text: 'Agent',
        value: 'agent.email',
        icon: 'mdi-account',
        type: FilterType.AUTOCOMPLETE,
        items: supportAgents.map((agent) => ({ text: agent.email, value: agent.email })),
        selected: { text: '', value: '' },
      },
    );
  }

  private addDepartmentsFilter(): void {
    this.filters.push(
      {
        text: 'Department',
        value: 'ticket.department',
        icon: 'mdi-account-multiple',
        type: FilterType.AUTOCOMPLETE,
        items: this.departments.map((department) => ({
          text: department,
          value: department.replace('&', '%26'),
        })),
        selected: { text: '', value: '' },
      },
    );
  }

  private addNumberFilter(): void {
    const numberFilter: FilterInterface = {
      text: 'Ticket Number',
      value: 'ticket.ticketNumber',
      icon: 'mdi-pound-box',
      type: FilterType.INPUT,
      items: [],
      selected: { text: 'ticket.ticketNumber', value: '' },
    };

    this.filters.push(numberFilter);
  }

  private addDatesFilter(): void {
    const dateFilter: FilterInterface = {
      text: 'Closed Date',
      value: 'ticket.closedDate',
      icon: 'mdi-calendar',
      type: FilterType.MENU,
      items: [
        { text: 'TODAY', value: 'today' },
        { text: 'YESTERDAY', value: 'yesterday' },
        { text: 'LAST 7 DAYS', value: '7 days' },
        { text: 'LAST 30 DAYS', value: '30 days' },
        { text: 'LAST MONTH', value: 'last month' },
        { text: 'CURRENT MONTH', value: 'current month' },
        { text: 'CUSTOM PERIOD', value: 'custom period' },
      ],
      selected: { text: '', value: '' },
    };

    this.filters.push(dateFilter);
  }

  private addDraftsFilter(): void {
    const draftFilter: FilterInterface = {
      text: 'Draft',
      value: 'review.isDraft',
      icon: 'mdi-progress-pencil',
      type: FilterType.CHECKBOX,
      items: [
        { text: 'True', value: true },
      ],
      selected: { text: 'review.isDraft', value: false },
    };

    this.filters.push(draftFilter);
  }

  private async applyFilter(): Promise<void> {
    const filterData: FilterRequestData = {};
    this.filters.forEach((filter) => {
      if (!filter.selected || filter.selected.text === '') {
        return;
      }

      if (filter.value === 'ticket.closedDate') {
        filterData[filter.value] = this.getDateFilterData();
        return;
      }

      filterData[filter.value] = filter.selected.value;
    });

    this.$emit('filter-tickets', filterData, this.filters);
  }

  private getDateFilterData(): string | undefined {
    const dateFilter = this.filters.find((filter) => filter.value === 'ticket.closedDate' && filter.selected);
    if (!dateFilter || !dateFilter.selected || !dateFilter.selected.value) {
      return undefined;
    }

    const selected = dateFilter.selected.value as string;

    let startDate: Date = new Date();
    let endDate: Date = new Date();
    let filterApplied = false;

    if (selected === 'today') {
      this.customDates = [];
      startDate = new Date();
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date();

      filterApplied = true;
    }

    if (selected === 'yesterday') {
      this.customDates = [];
      startDate = new Date();
      startDate.setDate(startDate.getDate() - 1);
      startDate.setHours(0, 0, 0, 0);

      endDate = new Date();
      endDate.setDate(endDate.getDate() - 1);
      endDate.setHours(23, 59, 59, 59);

      filterApplied = true;
    }

    if (selected.endsWith('days')) {
      this.customDates = [];
      const [count] = selected.split(' ');
      startDate = new Date();
      startDate.setDate(startDate.getDate() - Number(count));
      startDate.setHours(0, 0, 0, 0);

      endDate = new Date();
      endDate.setHours(23, 59, 59, 59);
      filterApplied = true;
    }

    if (selected === 'last month') {
      this.customDates = [];
      const date = new Date();

      startDate = new Date(date.getFullYear(), date.getMonth() - 1, 1);

      endDate = new Date(date.getFullYear(), date.getMonth(), 0);
      endDate.setHours(23, 59, 59, 59);
      filterApplied = true;
    }

    if (selected === 'current month') {
      this.customDates = [];
      const date = new Date();

      startDate = new Date(date.getFullYear(), date.getMonth(), 1);

      endDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      endDate.setHours(23, 59, 59, 59);
      filterApplied = true;
    }

    if (selected === 'custom period') {
      if (this.customDates.length < 2) {
        return undefined;
      }

      const [startDateStr, endDateStr] = this.customDates;

      startDate = new Date(startDateStr);
      startDate.setHours(0, 0, 0, 0);

      endDate = new Date(endDateStr);
      endDate.setHours(23, 59, 59, 59);
      filterApplied = true;
    }

    if (!filterApplied) {
      return undefined;
    }

    startDate.setMinutes(startDate.getTimezoneOffset());
    endDate.setMinutes(endDate.getTimezoneOffset());
    return `${this.convertDateToString(startDate)},${this.convertDateToString(endDate)}`;
  }

  private convertDateToString(date: Date): string {
    return date.toLocaleString('sv-se');
  }

  private async removeFilter(filter: FilterInterface, filterIndex: number): Promise<void> {
    this.selectedFilters[filterIndex].selected = { text: '', value: '' };
    this.selectedFilters = this.selectedFilters.filter((f) => f.text !== filter.text);
    await this.applyFilter();
  }
}
